import { useEffect, useRef } from 'react';

/**
 * Subscribes to the browser back/forward button (popstate) for the lifetime of
 * the calling component.
 *
 * The listener is registered once with a stable reference so the cleanup
 * actually removes it. Hand-rolled versions of this effect passed a freshly
 * allocated arrow function to removeEventListener, which removes nothing and
 * leaves the handler attached to window for the rest of the page load. Those
 * leaked handlers fired on every later back navigation -- including the
 * history.go(-1) behind react-router's navigate(-1) -- and yanked the user back
 * to whatever route the stale callback closed over.
 *
 * The callback is read through a ref, so it never goes stale either.
 */
export const useBackButton = (onBack: () => void): void => {
  const onBackRef = useRef(onBack);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    const handlePopState = () => onBackRef.current();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
};

export default useBackButton;
